import type { EncryptedPayload, RouteCustomer } from "./types";

const ITERATIONS = 250_000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const bytesToBase64 = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes));

const base64ToBytes = (value: string) =>
  Uint8Array.from(atob(value), (char) => char.charCodeAt(0));

const deriveKey = async (passphrase: string, salt: Uint8Array) => {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};

export const encryptJson = async <T>(
  value: T,
  passphrase: string,
): Promise<EncryptedPayload> => {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    key,
    encoder.encode(JSON.stringify(value)),
  );

  return {
    version: 1,
    algorithm: "AES-GCM",
    kdf: "PBKDF2-SHA-256",
    iterations: ITERATIONS,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    data: bytesToBase64(new Uint8Array(encrypted)),
    updatedAt: new Date().toISOString(),
  };
};

export const decryptJson = async <T>(
  payload: EncryptedPayload,
  passphrase: string,
): Promise<T> => {
  const salt = base64ToBytes(payload.salt);
  const iv = base64ToBytes(payload.iv);
  const key = await deriveKey(passphrase, salt);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    key,
    base64ToBytes(payload.data),
  );
  return JSON.parse(decoder.decode(decrypted)) as T;
};

export const createRouteFingerprint = async (customers: RouteCustomer[]) => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    encoder.encode(
      customers
        .map((customer) =>
          [
            customer.order,
            customer.customerNumber,
            customer.name,
          ].join("\u001f"),
        )
        .join("\u001e"),
    ),
  );
  return bytesToBase64(new Uint8Array(digest));
};
